import React, { useEffect, useState } from 'react';
import { Input, Loader, Message, Modal, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
    IAdminCompanyBillingUpdate,
    IAdminExtendRequest,
    IAnnualPlanRequest,
    IBillingDefaults,
} from '../../app/interfaces/IBilling';
import {
    cancelAnnualPlanThunk,
    clearAdminBillingError,
    closeBillingEvents,
    extendCompanyTrialThunk,
    getAdminBillingThunk,
    getBillingEventsThunk,
    selectAdminBillingState,
    setAnnualPlanThunk,
    startCompanyTrialThunk,
    updateBillingDefaultsThunk,
    updateCompanyBillingThunk,
} from '../../slices/adminBillingSlice';
import { selectUser } from '../../slices/userSlice';
import { describeDuration, formatDate, formatMoney } from '../../utils/Billing';
import BillingDefaultsForm from './BillingDefaultsForm';
import CompanyBillingRow from './CompanyBillingRow';

/*
* The Sub Ratings billing admin.
*
* Everything about the offer - the free trial, what an extension costs, how
* much time it buys - is editable here, account-wide or for one Procore company
* at a time. Staff only; the API refuses these routes to anyone else, and this
* screen refuses to render for them so they are not left staring at 403s.
*/
const BillingAdmin = () => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const { defaults, companies, events, eventsCompanyId, status, savingStatus, error } =
        useAppSelector(selectAdminBillingState);
    const [search, setSearch] = useState('');
    const [expandedId, setExpandedId] = useState<number | null>(null);

    useEffect(() => {
        if (user?.isStaff) {
            dispatch(getAdminBillingThunk(undefined));
        }
    }, [user?.isStaff]);

    // Refetch on search rather than filtering here, so the list stays right
    // however many companies there eventually are.
    useEffect(() => {
        if (!user?.isStaff) {
            return;
        }
        const timer = setTimeout(() => dispatch(getAdminBillingThunk(search || undefined)), 300);
        return () => clearTimeout(timer);
    }, [search]);

    if (user && !user.isStaff) {
        return (
            <Message
                header='Not available'
                content='Billing administration is limited to Sub Ratings staff.'
            />
        );
    }

    const saveDefaults = (update: Partial<IBillingDefaults>) => {
        dispatch(updateBillingDefaultsThunk(update));
    };

    const saveCompany = (companyId: number, update: IAdminCompanyBillingUpdate) => {
        dispatch(updateCompanyBillingThunk({ companyId, update }));
    };

    const extend = (companyId: number, request: IAdminExtendRequest) => {
        dispatch(extendCompanyTrialThunk({ companyId, request }));
    };

    const setAnnualPlan = (companyId: number, request: IAnnualPlanRequest) => {
        dispatch(setAnnualPlanThunk({ companyId, request }));
    };

    const eventsCompany = companies.find(c => c.id === eventsCompanyId);

    return (
        <div id='billing-admin'>
            <h1>Billing</h1>

            {error &&
                <Message
                    negative
                    onDismiss={() => dispatch(clearAdminBillingError())}
                    header='That did not save'
                    content={error.reason}
                />
            }

            {defaults &&
                <BillingDefaultsForm
                    defaults={defaults}
                    saving={savingStatus === 'saving'}
                    onSave={saveDefaults}
                />
            }

            <StyledTableHeader>
                <h3>Companies</h3>
                <Input
                    icon='search'
                    placeholder='Search by name'
                    value={search}
                    onChange={(_, { value }) => setSearch(value)}
                />
            </StyledTableHeader>

            {status === 'loading' && companies.length === 0 && <Loader active inline='centered' />}

            {status !== 'loading' && companies.length === 0 &&
                <Message content='No companies match that search.' />
            }

            {companies.length > 0 &&
                <Table selectable compact>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Company</Table.HeaderCell>
                            <Table.HeaderCell>Status</Table.HeaderCell>
                            <Table.HeaderCell>Access through</Table.HeaderCell>
                            <Table.HeaderCell>Remaining</Table.HeaderCell>
                            <Table.HeaderCell>Extension</Table.HeaderCell>
                            <Table.HeaderCell textAlign='right'>Paid to date</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {companies.map(company => (
                            <CompanyBillingRow
                                key={company.id}
                                company={company}
                                saving={savingStatus === 'saving'}
                                expanded={expandedId === company.id}
                                onToggle={() => setExpandedId(expandedId === company.id ? null : company.id)}
                                onSave={update => saveCompany(company.id, update)}
                                onExtend={request => extend(company.id, request)}
                                onStartTrial={() => dispatch(startCompanyTrialThunk({ companyId: company.id }))}
                                onShowHistory={() => dispatch(getBillingEventsThunk(company.id))}
                                onSetAnnualPlan={request => setAnnualPlan(company.id, request)}
                                onCancelAnnualPlan={immediate =>
                                    dispatch(cancelAnnualPlanThunk({ companyId: company.id, immediate }))}
                            />
                        ))}
                    </Table.Body>
                </Table>
            }

            {eventsCompany &&
                <Modal open closeIcon onClose={() => dispatch(closeBillingEvents())}>
                    <Modal.Header>Billing history - {eventsCompany.name}</Modal.Header>
                    <Modal.Content>
                        {events.length === 0
                            ? <p>Nothing recorded yet.</p>
                            : <Table basic='very' compact>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell>Date</Table.HeaderCell>
                                        <Table.HeaderCell>Event</Table.HeaderCell>
                                        <Table.HeaderCell>Status</Table.HeaderCell>
                                        <Table.HeaderCell>Time</Table.HeaderCell>
                                        <Table.HeaderCell textAlign='right'>Amount</Table.HeaderCell>
                                        <Table.HeaderCell>Ends</Table.HeaderCell>
                                        <Table.HeaderCell>By</Table.HeaderCell>
                                        <Table.HeaderCell>Note</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {events.map(event => (
                                        <Table.Row key={event.id}>
                                            <Table.Cell>{formatDate(event.completedAt || event.createdAt)}</Table.Cell>
                                            <Table.Cell>{event.kindLabel}</Table.Cell>
                                            <Table.Cell>{event.statusLabel}</Table.Cell>
                                            <Table.Cell>{event.days ? describeDuration(event.days) : '-'}</Table.Cell>
                                            <Table.Cell textAlign='right'>
                                                {event.amountCents ? formatMoney(event.amountCents, event.currency) : '-'}
                                            </Table.Cell>
                                            <Table.Cell>{formatDate(event.newExpiration)}</Table.Cell>
                                            <Table.Cell>{event.createdBy || 'Stripe'}</Table.Cell>
                                            <Table.Cell>{event.note}</Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        }
                    </Modal.Content>
                </Modal>
            }
        </div>
    );
};

const StyledTableHeader = styled('div')`
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin: 30px 0 10px;
    & h3 {
        margin: 0;
    }
`;

export default BillingAdmin;
